import { Howl, Howler } from 'howler';

/**
 * Sound effects for the wheel.
 *
 * Howls are created on first use rather than at import time: a Discord Activity
 * boots inside an iframe where audio is locked until the player interacts, and
 * building the pool eagerly would just queue up suspended contexts. Files are
 * generated by `npm run sounds` (see `scripts/generate-sounds.mjs`).
 */

export type SoundName = 'tick' | 'launch' | 'win' | 'jackpot' | 'lose' | 'click';

interface SoundDefinition {
  /** Base volume, balanced by ear against the others. */
  volume: number;
  /** Overlapping copies allowed. Ticks fire in bursts and must not cut each other. */
  pool?: number;
}

const SOUNDS: Record<SoundName, SoundDefinition> = {
  tick: { volume: 0.32, pool: 8 },
  launch: { volume: 0.5 },
  win: { volume: 0.6 },
  jackpot: { volume: 0.7 },
  lose: { volume: 0.45 },
  click: { volume: 0.35 },
};

const MUTE_KEY = 'spoyl.sound.muted';

const cache = new Map<SoundName, Howl>();
let muted = readMuted();

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

function load(name: SoundName): Howl {
  const cached = cache.get(name);
  if (cached) return cached;

  const definition = SOUNDS[name];
  const howl = new Howl({
    src: [`/sounds/${name}.wav`],
    volume: definition.volume,
    pool: definition.pool ?? 3,
    preload: true,
  });
  cache.set(name, howl);
  return howl;
}

export interface PlayOptions {
  /** Playback rate. Used to pitch ticks up as the wheel accelerates. */
  rate?: number;
  /** Multiplier applied on top of the sound's base volume. */
  volume?: number;
}

export const sfx = {
  play(name: SoundName, options: PlayOptions = {}): void {
    if (muted) return;
    try {
      const howl = load(name);
      const id = howl.play();
      if (options.rate !== undefined) howl.rate(clamp(options.rate, 0.5, 4), id);
      if (options.volume !== undefined) {
        howl.volume(SOUNDS[name].volume * clamp(options.volume, 0, 1), id);
      }
    } catch {
      // Audio is a garnish — never let it break a spin.
    }
  },

  /** Warms the pool so the first tick of a spin isn't late. */
  preload(...names: SoundName[]): void {
    if (muted) return;
    for (const name of names) load(name);
  },

  isMuted(): boolean {
    return muted;
  },

  setMuted(next: boolean): void {
    muted = next;
    Howler.mute(next);
    try {
      localStorage.setItem(MUTE_KEY, next ? '1' : '0');
    } catch {
      // Preference stays for this session only.
    }
  },

  /** Stops everything immediately — used when a spin is interrupted. */
  stopAll(): void {
    for (const howl of cache.values()) howl.stop();
  },
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// Apply the persisted preference to the global mixer as soon as this module loads.
Howler.mute(muted);
